import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { urlWithoutAuthenticationError } from "../features/auth/auth-url";
import { asProcurementRole } from "../features/procurement/domain/models";
import { defaultProcurementWorkspaceTarget, type ProcurementWorkspaceTarget } from "../features/procurement/domain/selectors";
import { ApiError, apiRequest, clearApiResponseCache, getRolePreview as getApiRolePreview, setRolePreview as setApiRolePreview } from "../shared/api/client";
import {
  can,
  firstAvailableView,
  navPermission,
  roleCanNavigate,
  type SessionUser,
} from "../shared/auth/access";
import { useGlobalSelectBehavior } from "../shared/components/useGlobalSelectBehavior";
import {
  fallbackUser,
  navItems,
  secondaryNavItems,
  type PreviewRole,
  type WorkingRole,
} from "./navigation";

const MINIMUM_BACKGROUND_REFRESH_DELAY_MS = 30_000;

type GlobalSearchResult = {
  id: string;
  type: "request" | "supplier" | "document";
  title: string;
  subtitle: string;
  requestNo?: string;
};

export function useApplicationShell() {
  useGlobalSelectBehavior();
  const [user, setUser] = useState<SessionUser>(fallbackUser);
  const [realUser, setRealUser] = useState<SessionUser | null>(null);
  const [rolePreview, setRolePreview] = useState<PreviewRole | null>(
    () => getApiRolePreview() as PreviewRole | null,
  );
  const [rolePreviewAvailable, setRolePreviewAvailable] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState<
    GlobalSearchResult[]
  >([]);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [globalSearchError, setGlobalSearchError] = useState("");
  const globalSearchInputRef = useRef<HTMLInputElement>(null);
  const globalSearchRootRef = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(
    () => window.matchMedia("(min-width: 981px)").matches,
  );
  const [sidebarPinned, setSidebarPinned] = useState(
    () =>
      window.matchMedia("(min-width: 981px)").matches &&
      window.localStorage.getItem("anl-sidebar-pinned") !== "false",
  );
  const [activeNav, setActiveNav] = useState("Erişim Yok");
  const activeNavRef = useRef(activeNav);
  const [navigationHistory, setNavigationHistory] = useState<string[]>([]);
  const nestedBackRef = useRef<(() => void) | null>(null);
  const [nestedBackAvailable, setNestedBackAvailable] = useState(false);
  const [offerFocusRequestNo, setOfferFocusRequestNo] = useState("");
  const [purchaseOrderFocusRequestNo, setPurchaseOrderFocusRequestNo] =
    useState("");
  const [approvalFocusRequestNo, setApprovalFocusRequestNo] = useState("");
  const [requestFocusRequestNo, setRequestFocusRequestNo] = useState(
    () =>
      new URLSearchParams(window.location.search).get("request")?.trim() ?? "",
  );
  const [supplierFocusQuery, setSupplierFocusQuery] = useState("");
  const [globalNavigationVersion, setGlobalNavigationVersion] = useState(0);
  const [dataRefreshVersion, setDataRefreshVersion] = useState(0);
  const backgroundedAtRef = useRef<number | null>(null);
  const sessionRevalidationInFlightRef = useRef(false);
  const [toast, setToast] = useState<{
    message: string;
    tone: "success" | "error";
  }>({ message: "", tone: "success" });
  const toastTimeoutRef = useRef<number | null>(null);
  const [requestComposerOpen, setRequestComposerOpen] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const [sessionLoadVersion, setSessionLoadVersion] = useState(0);
  const [roleSwitching, setRoleSwitching] = useState(false);
  const rolePreviewReturnNavRef = useRef<string | null>(null);
  const resetNavigation = useCallback((view: string) => {
    nestedBackRef.current = null;
    setNestedBackAvailable(false);
    activeNavRef.current = view;
    setActiveNav(view);
    setNavigationHistory([]);
  }, []);
  const navigateTo = useCallback((view: string) => {
    const currentView = activeNavRef.current;
    if (currentView === view) return;
    if (currentView !== "Erişim Yok") {
      setNavigationHistory((history) => [...history, currentView].slice(-30));
    }
    nestedBackRef.current = null;
    setNestedBackAvailable(false);
    activeNavRef.current = view;
    setActiveNav(view);
  }, []);
  const openRequestWorkspace = useCallback(
    (requestNo: string, target: ProcurementWorkspaceTarget) => {
      setGlobalNavigationVersion((current) => current + 1);
      if (target === "offers") {
        setOfferFocusRequestNo(requestNo);
        navigateTo("Teklifler");
        return;
      }
      if (target === "approvals") {
        setApprovalFocusRequestNo(requestNo);
        navigateTo("Talepler");
        return;
      }
      setRequestFocusRequestNo(requestNo);
      navigateTo("Talepler");
    },
    [navigateTo],
  );
  const openPurchaseOrder = useCallback(
    (requestNo: string) => {
      setPurchaseOrderFocusRequestNo(requestNo);
      setGlobalNavigationVersion((current) => current + 1);
      navigateTo("Siparişler");
    },
    [navigateTo],
  );
  const returnSpecialistToHome = useCallback(() => {
    setOfferFocusRequestNo("");
    setRequestFocusRequestNo("");
    setDataRefreshVersion((current) => current + 1);
    setGlobalNavigationVersion((current) => current + 1);
    resetNavigation(firstAvailableView(user));
  }, [resetNavigation, user]);
  const visibleNavItems = useMemo(
    () =>
      navItems.filter(
        (item) =>
          can(user, navPermission[item.label]) &&
          roleCanNavigate(user, item.label),
      ),
    [user],
  );
  const visibleSecondaryNavItems = useMemo(
    () =>
      secondaryNavItems.filter(
        (item) =>
          can(user, navPermission[item.label]) &&
          roleCanNavigate(user, item.label),
      ),
    [user],
  );
  const sidebarPrimaryItems =
    user.role === "SISTEM_YONETICISI"
      ? [
        ...visibleNavItems,
        ...visibleSecondaryNavItems.filter(
          (item) => item.label !== "Profilim",
        ),
      ]
      : visibleNavItems;
  const sidebarSecondaryItems =
    user.role === "SISTEM_YONETICISI"
      ? visibleSecondaryNavItems.filter((item) => item.label === "Profilim")
      : visibleSecondaryNavItems;
  const previousNav = useMemo(
    () =>
      [...navigationHistory]
        .reverse()
        .find(
          (view) =>
            roleCanNavigate(user, view) && can(user, navPermission[view]),
        ) ?? null,
    [navigationHistory, user],
  );
  const goBack = useCallback(() => {
    if (nestedBackRef.current) {
      const nestedBack = nestedBackRef.current;
      nestedBackRef.current = null;
      setNestedBackAvailable(false);
      nestedBack();
      return;
    }
    if (!previousNav) return;
    const previousIndex = navigationHistory.lastIndexOf(previousNav);
    setNavigationHistory(navigationHistory.slice(0, previousIndex));
    activeNavRef.current = previousNav;
    setActiveNav(previousNav);
    setGlobalSearchOpen(false);
    setRequestComposerOpen(false);
    if (!sidebarPinned) setSidebarOpen(false);
  }, [navigationHistory, previousNav, sidebarPinned]);
  const handleNestedBackChange = useCallback((handler: (() => void) | null) => {
    nestedBackRef.current = handler;
    setNestedBackAvailable(Boolean(handler));
  }, []);
  useEffect(() => {
    let active = true;
    const linkedRequestNo = new URLSearchParams(window.location.search)
      .get("request")
      ?.trim();
    const linkedView = new URLSearchParams(window.location.search).get("view");

    const loadSession = async () => {
      for (let attempt = 0;attempt < 5;attempt += 1) {
        try {
          const session = await apiRequest<SessionUser>("/api/session");
          if (!active) return;
          if (session.role === "GUEST") {
            setSessionUnavailable(false);
            setAuthRequired(true);
            setUser(fallbackUser);
            resetNavigation("Erişim Yok");
            setSessionReady(true);
            return;
          }
          const cleanAuthenticationUrl = urlWithoutAuthenticationError(window.location.href);
          if (cleanAuthenticationUrl) {
            window.history.replaceState(window.history.state, "", cleanAuthenticationUrl);
          }
          setSessionUnavailable(false);
          setAuthRequired(false);
          setUser(session);
          setRealUser(session.preview ? null : session);
          if (linkedRequestNo) {
            const target = defaultProcurementWorkspaceTarget(
              asProcurementRole(session.role),
            );
            if (target === "offers" && roleCanNavigate(session, "Teklifler")) {
              setOfferFocusRequestNo(linkedRequestNo);
              setGlobalNavigationVersion((current) => current + 1);
              resetNavigation("Teklifler");
            } else if (
              target === "approvals" &&
              roleCanNavigate(session, "Talepler")
            ) {
              setApprovalFocusRequestNo(linkedRequestNo);
              setGlobalNavigationVersion((current) => current + 1);
              resetNavigation("Talepler");
            } else if (roleCanNavigate(session, "Talepler")) {
              setRequestFocusRequestNo(linkedRequestNo);
              setGlobalNavigationVersion((current) => current + 1);
              resetNavigation("Talepler");
            } else {
              resetNavigation(firstAvailableView(session));
            }
          } else {
            resetNavigation(
              linkedView && roleCanNavigate(session, linkedView) && can(session, navPermission[linkedView])
                ? linkedView
                : firstAvailableView(session),
            );
          }
          setSessionReady(true);
          return;
        } catch (error) {
          if (!active) return;
          const storedRolePreview = getApiRolePreview();
          if (
            attempt === 0 &&
            storedRolePreview &&
            error instanceof ApiError &&
            ["HTTP_400", "HTTP_401", "HTTP_403", "HTTP_404"].includes(
              error.code,
            )
          ) {
            setApiRolePreview(null);
            setRolePreview(null);
            continue;
          }
          if (attempt < 4) {
            await new Promise((resolve) =>
              window.setTimeout(resolve, 300 * 2 ** attempt),
            );
            continue;
          }
          setUser(fallbackUser);
          resetNavigation("Erişim Yok");
          setAuthRequired(false);
          setSessionUnavailable(true);
          setSessionReady(true);
        }
      }
    };

    void loadSession();
    return () => {
      active = false;
    };
  }, [resetNavigation, sessionLoadVersion]);
  useEffect(() => {
    if (!sessionReady) return;

    const markBackgrounded = () => {
      backgroundedAtRef.current = Date.now();
    };
    const revalidateVisiblePage = async () => {
      if (document.visibilityState === "hidden") return;
      if (backgroundedAtRef.current === null) return;
      const backgroundDuration = Date.now() - backgroundedAtRef.current;
      backgroundedAtRef.current = null;
      if (backgroundDuration < MINIMUM_BACKGROUND_REFRESH_DELAY_MS) return;
      if (sessionRevalidationInFlightRef.current) return;

      sessionRevalidationInFlightRef.current = true;
      try {
        const session = await apiRequest<SessionUser>("/api/session", {
          cache: "no-store",
        });
        if (session.role === "GUEST") {
          setAuthRequired(true);
          setUser(fallbackUser);
          resetNavigation("Erişim Yok");
          return;
        }
        setAuthRequired(false);
        setUser(session);
        if (!rolePreview) setRealUser(session);
      } catch {
        setToast({
          message:
            "Oturum ve güncel veriler doğrulanamadı. Bağlantınızı kontrol edin.",
          tone: "error",
        });
      } finally {
        sessionRevalidationInFlightRef.current = false;
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        markBackgrounded();
      } else {
        void revalidateVisiblePage();
      }
    };
    const handleFocus = () => void revalidateVisiblePage();

    window.addEventListener("blur", markBackgrounded);
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("blur", markBackgrounded);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [resetNavigation, rolePreview, sessionReady]);
  useEffect(() => {
    if (!sessionReady || realUser?.role !== "SISTEM_YONETICISI") return;
    apiRequest<{ enabled: boolean; }>("/api/role-preview/status")
      .then(({ enabled }) => setRolePreviewAvailable(enabled))
      .catch(() => setRolePreviewAvailable(false));
  }, [realUser, sessionReady]);
  const activateRolePreview = async (role: PreviewRole) => {
    rolePreviewReturnNavRef.current = activeNavRef.current;
    setApiRolePreview(role);
    try {
      const previewUser = await apiRequest<SessionUser>("/api/session");
      setRolePreview(role);
      setUser(previewUser);
      resetNavigation(firstAvailableView(previewUser));
      setSidebarOpen(false);
      notify("İzole rol önizleme çalışma alanı açıldı.");
    } catch (error) {
      rolePreviewReturnNavRef.current = null;
      setApiRolePreview(null);
      setRolePreview(null);
      notify(
        error instanceof Error
          ? error.message
          : "Rol önizleme modu başlatılamadı.",
      );
    }
  };
  const exitRolePreview = async () => {
    setApiRolePreview(null);
    setRolePreview(null);
    const session = realUser ?? (await apiRequest<SessionUser>("/api/session"));
    setUser(session);
    setRealUser(session);
    const returnView = rolePreviewReturnNavRef.current;
    rolePreviewReturnNavRef.current = null;
    resetNavigation(
      returnView && roleCanNavigate(session, returnView) && can(session, navPermission[returnView])
        ? returnView
        : firstAvailableView(session),
    );
    setSidebarOpen(false);
    notify("Rol önizleme modu kapatıldı.");
  };
  const switchActiveRole = async (role: WorkingRole) => {
    if (role === user.role || roleSwitching) return;
    setRoleSwitching(true);
    setApiRolePreview(null);
    setRolePreview(null);
    clearApiResponseCache();
    try {
      await apiRequest<void>("/api/session/active-role", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      const session = await apiRequest<SessionUser>("/api/session");
      setUser(session);
      setRealUser(session);
      resetNavigation(firstAvailableView(session));
      setRolePreviewAvailable(false);
      setSidebarOpen(window.matchMedia("(min-width: 981px)").matches);
      notify(`Çalışma rolü ${session.roleLabel} olarak değiştirildi.`);
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "Çalışma rolü değiştirilemedi.",
      );
    } finally {
      setRoleSwitching(false);
    }
  };
  const notify = useCallback((message: string, explicitTone?: "success" | "error") => {
    const tone = explicitTone ?? (
      /(bulunamadı|yüklenemedi|başarısız|geçersiz|yapılamadı|engellendi|hata|zorunludur|error|failed|invalid|cannot|inconsistent|parameter)/i.test(
        message,
      )
        ? "error"
        : "success");
    if (toastTimeoutRef.current !== null)
      window.clearTimeout(toastTimeoutRef.current);
    setToast({ message, tone });
    toastTimeoutRef.current = window.setTimeout(() => {
      setToast({ message: "", tone: "success" });
      toastTimeoutRef.current = null;
    }, tone === "error" ? 12000 : 6000);
  }, []);
  useEffect(
    () => () => {
      if (toastTimeoutRef.current !== null)
        window.clearTimeout(toastTimeoutRef.current);
    },
    [],
  );
  useEffect(() => {
    const searchTerm = globalSearchTerm.trim();
    if (searchTerm.length < 2) {
      setGlobalSearchResults([]);
      setGlobalSearchLoading(false);
      setGlobalSearchError("");
      return;
    }

    let active = true;
    setGlobalSearchLoading(true);
    setGlobalSearchError("");
    const timeout = window.setTimeout(() => {
      apiRequest<GlobalSearchResult[]>(
        `/api/search?q=${encodeURIComponent(searchTerm)}`,
      )
        .then((results) => {
          if (active) setGlobalSearchResults(results);
        })
        .catch(() => {
          if (!active) return;
          setGlobalSearchResults([]);
          setGlobalSearchError("Arama sonuçları alınamadı.");
        })
        .finally(() => {
          if (active) setGlobalSearchLoading(false);
        });
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [globalSearchTerm, user.role]);
  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLocaleLowerCase("tr") === "k"
      ) {
        event.preventDefault();
        globalSearchInputRef.current?.focus();
        setGlobalSearchOpen(true);
      }
      if (
        event.key === "Escape" &&
        document.activeElement === globalSearchInputRef.current
      ) {
        setGlobalSearchOpen(false);
        globalSearchInputRef.current?.blur();
      }
    };
    const handleOutsideClick = (event: PointerEvent) => {
      if (
        globalSearchRootRef.current &&
        !globalSearchRootRef.current.contains(event.target as Node)
      ) {
        setGlobalSearchOpen(false);
      }
    };
    document.addEventListener("keydown", handleShortcut);
    document.addEventListener("pointerdown", handleOutsideClick);
    return () => {
      document.removeEventListener("keydown", handleShortcut);
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, []);
  const openGlobalSearchResult = (result: GlobalSearchResult) => {
    if (result.type === "supplier") {
      setGlobalNavigationVersion((current) => current + 1);
      setSupplierFocusQuery(result.title);
      navigateTo("Tedarikçiler");
    } else {
      openRequestWorkspace(
        result.requestNo ?? result.id,
        defaultProcurementWorkspaceTarget(asProcurementRole(user.role)),
      );
    }
    setGlobalSearchTerm("");
    setGlobalSearchResults([]);
    setGlobalSearchOpen(false);
  };
  const closeSidebarAfterPointerLeave = () => {
    if (
      !sidebarPinned &&
      window.matchMedia("(min-width: 981px) and (hover: hover)").matches
    ) {
      setSidebarOpen(false);
    }
  };
  const closeSidebar = () => {
    window.localStorage.removeItem("anl-sidebar-pinned");
    setSidebarPinned(false);
    setSidebarOpen(false);
  };
  const toggleSidebarPin = () => {
    setSidebarPinned((current) => {
      const next = !current;
      window.localStorage.setItem("anl-sidebar-pinned", String(next));
      setSidebarOpen(next);
      return next;
    });
  };
  const logout = useCallback(async () => {
    await apiRequest<void>("/auth/logout", { method: "POST" });
    clearApiResponseCache();
    setUser(fallbackUser);
    setRealUser(null);
    setApiRolePreview(null);
    setRolePreview(null);
    setAuthRequired(true);
  }, []);
  return {
    user,
    rolePreview,
    rolePreviewAvailable,
    sessionReady,
    setSessionReady,
    globalSearchTerm,
    setGlobalSearchTerm,
    globalSearchResults,
    globalSearchOpen,
    setGlobalSearchOpen,
    globalSearchLoading,
    globalSearchError,
    globalSearchInputRef,
    globalSearchRootRef,
    sidebarOpen,
    setSidebarOpen,
    sidebarPinned,
    activeNav,
    nestedBackAvailable,
    offerFocusRequestNo,
    purchaseOrderFocusRequestNo,
    approvalFocusRequestNo,
    requestFocusRequestNo,
    supplierFocusQuery,
    globalNavigationVersion,
    dataRefreshVersion,
    toast,
    dismissToast: () => {
      if (toastTimeoutRef.current !== null) window.clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
      setToast({ message: "", tone: "success" });
    },
    requestComposerOpen,
    setRequestComposerOpen,
    authRequired,
    sessionUnavailable,
    setSessionUnavailable,
    setSessionLoadVersion,
    roleSwitching,
    navigateTo,
    openRequestWorkspace,
    openPurchaseOrder,
    returnSpecialistToHome,
    sidebarPrimaryItems,
    sidebarSecondaryItems,
    previousNav,
    goBack,
    handleNestedBackChange,
    activateRolePreview,
    exitRolePreview,
    switchActiveRole,
    notify,
    openGlobalSearchResult,
    closeSidebarAfterPointerLeave,
    closeSidebar,
    toggleSidebarPin,
    logout,
  };
}
